import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage, languages } from '../i18n';
import { CraftFoodLogo } from '../components/brand/Logos';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Globe, ArrowLeft, Upload, Building2, MapPin, FileText } from 'lucide-react';

export default function RegisterPage() {
  const { t, language, setLanguage } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [licensePreview, setLicensePreview] = useState(null);
  const currentLang = languages.find(l => l.code === language);

  const [form, setForm] = useState({
    company_name: '', contact_person: '', phone: '', email: '', password: '',
    delivery_address: '', delivery_recipient_name: '', delivery_recipient_phone: '',
    delivery_notes: '', business_license: null,
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Файл слишком большой (макс 5MB)'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm({ ...form, business_license: event.target.result });
      setLicensePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_license) {
      if (!window.confirm('사업자등록증 не загружено. Продолжить без него?')) return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register-company`, {
        ...form,
        client_type: 'store',
        preferred_language: language,
      });
      toast.success('Welcome to CRAFTFOOD');
      const user = await login(form.email, form.password);
      if (user.role === 'client_user') navigate('/client');
      else navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка регистрации');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F6F4EE] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-testid="reg-back">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="cff-button-outline" data-testid="reg-lang-selector">
                <Globe className="h-4 w-4 mr-1" /> {currentLang?.flag} {currentLang?.name}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map(lang => (
                <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}>
                  <span className="mr-2">{lang.flag}</span> {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <CraftFoodLogo size="md" linkTo={null} className="mb-10" />
        <p className="eyebrow mb-3">{t('register')}</p>
        <h1 className="h-display text-4xl md:text-5xl mb-12">
          Become a <span className="text-[#C9A352]">partner.</span>
        </h1>

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Company */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="h-5 w-5 text-[#556B2F]" />
              <h3 className="eyebrow">Company information</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Company name <span className="text-[#8E2A4E]">*</span></Label>
                <Input required value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" placeholder="Seoul Bakery" data-testid="reg-company-name" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Contact person <span className="text-[#8E2A4E]">*</span></Label>
                  <Input required value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" data-testid="reg-contact" />
                </div>
                <div>
                  <Label className="text-sm">Phone <span className="text-[#8E2A4E]">*</span></Label>
                  <Input required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" placeholder="010-1234-5678" data-testid="reg-phone" />
                </div>
                <div>
                  <Label className="text-sm">Email <span className="text-[#8E2A4E]">*</span></Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" data-testid="reg-email" />
                </div>
                <div>
                  <Label className="text-sm">Password <span className="text-[#8E2A4E]">*</span></Label>
                  <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" data-testid="reg-password" />
                </div>
              </div>
            </div>
          </section>

          {/* Delivery */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="h-5 w-5 text-[#556B2F]" />
              <h3 className="eyebrow">Delivery address</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Full address <span className="text-[#8E2A4E]">*</span></Label>
                <Textarea required rows={2} value={form.delivery_address} onChange={(e) => setForm({...form, delivery_address: e.target.value})} className="mt-2 border-[#1A1A1A]/15 focus:ring-[#556B2F]" data-testid="reg-address" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Recipient name <span className="text-[#8E2A4E]">*</span></Label>
                  <Input required value={form.delivery_recipient_name} onChange={(e) => setForm({...form, delivery_recipient_name: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" data-testid="reg-recipient" />
                </div>
                <div>
                  <Label className="text-sm">Recipient phone <span className="text-[#8E2A4E]">*</span></Label>
                  <Input required value={form.delivery_recipient_phone} onChange={(e) => setForm({...form, delivery_recipient_phone: e.target.value})} className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]" data-testid="reg-recipient-phone" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Delivery notes</Label>
                <Textarea rows={2} value={form.delivery_notes} onChange={(e) => setForm({...form, delivery_notes: e.target.value})} className="mt-2 border-[#1A1A1A]/15 focus:ring-[#556B2F]" placeholder="Call before arrival, etc." />
              </div>
            </div>
          </section>

          {/* License */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <FileText className="h-5 w-5 text-[#556B2F]" />
              <h3 className="eyebrow">Business license (사업자등록증)</h3>
            </div>
            <div className="border-2 border-dashed border-[#1A1A1A]/15 rounded-xl p-8 text-center hover:border-[#556B2F] transition-colors bg-[#F2EFE6]/40">
              {licensePreview ? (
                <div>
                  <img src={licensePreview} alt="License" className="max-h-40 mx-auto rounded mb-3" />
                  <button type="button" onClick={() => { setLicensePreview(null); setForm({...form, business_license: null}); }} className="text-xs text-[#8E2A4E] hover:underline">Remove</button>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-[#1A1A1A]/30 mx-auto mb-3" />
                  <input type="file" accept="image/*,.pdf" onChange={handleFileUpload} className="hidden" id="license-upload" data-testid="reg-license-upload" />
                  <label htmlFor="license-upload" className="cursor-pointer text-sm text-[#556B2F] font-bold hover:underline">Choose file</label>
                  <p className="text-xs text-[#1A1A1A]/40 mt-1">JPG, PNG, PDF · max 5MB</p>
                </>
              )}
            </div>
          </section>

          <Button type="submit" disabled={loading} className="w-full cff-button-primary h-14 text-base" data-testid="reg-submit-btn">
            {loading ? '...' : 'Register & sign in'}
          </Button>
          <p className="text-xs text-[#1A1A1A]/45 text-center">
            You'll be signed in immediately and can place orders right away.<br/>
            Admin will receive a notification about your registration.
          </p>
        </form>
      </div>
    </div>
  );
}
